import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AtSign, Check, X, ExternalLink } from "lucide-react";

export default function NexaProfileSection() {
  const { user, checkAuth } = useAuth();
  const vis = user?.profile_visibility || {};
  const prof = user?.profile || {};
  const [handle, setHandle] = useState(user?.nexa_id || "");
  const [savedHandle, setSavedHandle] = useState(user?.nexa_id || "");
  const [handleStatus, setHandleStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({
    headline: prof.headline || "",
    bio: prof.bio || "",
    city: prof.city || "",
    country: prof.country || "",
    visibility_profile: vis.profile || "public",
    show_reputation: vis.reputation !== false,
    show_verifications: vis.verifications !== false,
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const checkHandle = async () => {
    setChecking(true);
    setHandleStatus(null);
    try {
      const res = await api.get("/users/handle-availability", { params: { handle } });
      setHandleStatus((res.data.available || handle.toLowerCase() === savedHandle) ? "available" : res.data.reason);
    } catch {
      setHandleStatus("error");
    } finally {
      setChecking(false);
    }
  };

  const saveHandle = async () => {
    setError("");
    setMessage("");
    try {
      const res = await api.patch("/users/me/handle", { handle });
      setSavedHandle(res.data.nexa_id);
      setHandle(res.data.nexa_id);
      setHandleStatus(null);
      setMessage("Identifiant NEXA mis à jour.");
      checkAuth?.();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSaving(true);
    try {
      await api.patch("/users/me/profile", form);
      setMessage("Profil NEXA enregistré.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="nexa-profile-section" className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-brand-navy flex items-center gap-2">
          <AtSign className="w-5 h-5 text-brand-turquoise" />
          Mon identité NEXA
        </h2>
        {savedHandle && (
          <Link
            data-testid="public-profile-link"
            to={`/profil/${savedHandle}`}
            className="text-xs font-medium text-brand-turquoise hover:underline flex items-center gap-1"
          >
            Voir mon profil public <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>

      {error && <div data-testid="nexa-profile-error" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}
      {message && <div data-testid="nexa-profile-success" className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">{message}</div>}

      <div className="mb-5">
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Identifiant NEXA (@handle)</label>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <div className="flex items-center flex-1 min-w-56 border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-turquoise">
            <span className="pl-3 text-slate-400 text-sm">@</span>
            <input
              data-testid="handle-input"
              value={handle}
              onChange={(e) => { setHandle(e.target.value); setHandleStatus(null); }}
              placeholder="votre.identifiant"
              className="flex-1 px-2 py-2.5 text-sm focus:outline-none"
            />
          </div>
          <button
            type="button"
            data-testid="handle-check-button"
            onClick={checkHandle}
            disabled={checking || !handle}
            className="text-xs font-medium text-brand-navy border border-slate-300 rounded-lg px-3 py-2.5 hover:bg-slate-50 disabled:opacity-50"
          >
            {checking ? "…" : "Vérifier"}
          </button>
          <button
            type="button"
            data-testid="handle-save-button"
            onClick={saveHandle}
            disabled={!handle || handle.toLowerCase() === savedHandle}
            className="text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-4 py-2.5 hover:bg-brand-navy disabled:opacity-50"
          >
            Enregistrer
          </button>
          {handleStatus === "available" && (
            <span data-testid="handle-available" className="flex items-center gap-1 text-xs text-green-700"><Check className="w-3.5 h-3.5" />Disponible</span>
          )}
          {handleStatus === "taken" && (
            <span data-testid="handle-taken" className="flex items-center gap-1 text-xs text-red-600"><X className="w-3.5 h-3.5" />Déjà pris</span>
          )}
          {handleStatus === "invalid" && (
            <span data-testid="handle-invalid" className="flex items-center gap-1 text-xs text-amber-600"><X className="w-3.5 h-3.5" />Format invalide (3-30 caractères : a-z, 0-9, . _ -)</span>
          )}
        </div>
      </div>

      <form onSubmit={saveProfile} className="space-y-3">
        <input data-testid="profile-headline-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Titre professionnel (ex. Soudeur certifié — 8 ans d'expérience)" className={inputCls} />
        <textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio publique" rows={3} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="profile-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className={inputCls} />
          <input data-testid="profile-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Pays" className={inputCls} />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select data-testid="profile-visibility-select" value={form.visibility_profile} onChange={(e) => setForm({ ...form, visibility_profile: e.target.value })} className={`${inputCls} bg-white`}>
            <option value="public">Profil : public</option>
            <option value="members">Profil : membres NEXA</option>
            <option value="private">Profil : privé</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input data-testid="profile-show-reputation" type="checkbox" checked={form.show_reputation} onChange={(e) => setForm({ ...form, show_reputation: e.target.checked })} className="w-4 h-4 accent-brand-turquoise" />
            Afficher ma réputation
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input data-testid="profile-show-verifications" type="checkbox" checked={form.show_verifications} onChange={(e) => setForm({ ...form, show_verifications: e.target.checked })} className="w-4 h-4 accent-brand-turquoise" />
            Afficher mes vérifications
          </label>
        </div>
        <button
          data-testid="profile-save-button"
          type="submit"
          disabled={saving}
          className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-blue transition-colors duration-200 disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Enregistrer mon profil NEXA"}
        </button>
      </form>
    </div>
  );
}
